<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		DashLayout,
		CardGrid,
		DashWidgetContainer,
		DashWidgetGrid,
		Icon,
		Text,
		Button,
		DashActionArea,
	} from '@Shelly/shelly-design-system';
	import { edit } from 'src/stores/ui/edit_lists';
	import { routeParams } from 'src/stores/ui/viewport';
	import DashWidgetCard from 'src/components/Dashboards/DashWidgets/DashWidgetCard.svelte';
	import { dashboards } from 'src/stores/features/dashboards';
	import { sortBy } from '../list-helpers';
	import {
		getDashWidgetGridConfig,
		mapDashItemsToDashWidgets,
		requestReorderDashboardWidget,
		staticCardGridConfig,
	} from 'src/components/Dashboards/dash-helpers';
	import DashWidgetMoreBtn from './Components/DashWidgetMoreBtn.svelte';
	import { onDestroy } from 'svelte';
	import DashWidgetActionModal from 'src/components/AddActions/Wizards/DashWidgetActionModal.svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	export let list = [];
	export let id;

	const flipDurationMs = 0;
	let dragDisabledLeft = true;
	let dragDisabledRight = true;
	let isMobile = false;
	let showAddWidget = false;
	let windowInnerWidth: number;
	let editColumn: 'left' | 'right' = 'left';
	let hideSelectDash = false;

	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;

	$: currentDash = $dashboards[id];

	$: allWidgets = currentDash
		? [...mapDashItemsToDashWidgets(currentDash, id)]
				.sort((x, y) => sortBy(x, y, 'position'))
				.filter((item) => item?.cards?.length > 0)
		: [];

	$: dashItemsLeft = $edit
		? dashItemsLeft
		: [...(allWidgets?.filter((item) => item?.col === 'left') || [])];

	$: dashItemsRight = $edit
		? dashItemsRight
		: [...(allWidgets?.filter((item) => item?.col === 'right') || [])];

	//#region LEFT COLUMN CONTROL
	const handleReorderLeft = (e: CustomEvent) => {
		dashItemsLeft = e.detail.items;
	};

	const handleReorderLeftEnd = async (e: CustomEvent) => {
		dashItemsLeft = e.detail.items.map((item, i) => {
			return {
				...item,
				col: 'left',
				position: i,
			};
		});

		handleEndDragLeft();

		// Send request
		const allWidgets = [...dashItemsLeft, ...dashItemsRight];
		let dashWidgets = {};

		allWidgets.map((v, i) => {
			delete v?.isDndShadowItem;
			dashWidgets[v.id] = {
				...v,
				cards: v.cards.map((item, y) => {
					return {
						id: item.id,
						_type: item._type,
						position: y,
					};
				}),
			};
		});

		await requestReorderDashboardWidget(id, dashWidgets);
	};

	const handleStartDragLeft = () => {
		dragDisabledLeft = false;
	};

	const handleEndDragLeft = () => {
		dragDisabledLeft = true;
	};
	//#regionend LEFT COLUMN CONTROL

	//#region RIGHT COLUMN CONTROL
	const handleReorderRight = (e: CustomEvent) => {
		dashItemsRight = e.detail.items;
	};

	const handleReorderRightEnd = async (e: CustomEvent) => {
		dashItemsRight = e.detail.items.map((item, i) => {
			return {
				...item,
				col: 'right',
				position: i,
			};
		});

		handleEndDragRight();

		// Send request
		const allWidgets = [...dashItemsLeft, ...dashItemsRight];
		let dashWidgets = {};

		allWidgets.map((v, i) => {
			delete v?.isDndShadowItem;
			dashWidgets[v.id] = {
				...v,
				cards: v.cards.map((item, y) => {
					return {
						id: item.id,
						_type: item._type,
						position: y,
					};
				}),
			};
		});

		await requestReorderDashboardWidget(id, dashWidgets);
	};

	const handleStartDragRight = () => {
		dragDisabledRight = false;
	};

	const handleEndDragRight = () => {
		dragDisabledRight = true;
	};
	//#regionend RIGHT COLUMN CONTROL

	onDestroy(() => {
		edit.set(false);
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if allWidgets.length === 0}
	<div class="d-flex flex-direction-column align-items-center gap-m pt-md-l pt-sm-m">
		<Icon size="l" icon="fal fa-grid-2-plus" />
		<div class="d-flex flex-direction-column align-items-center gap-s">
			<Text class="text-center" element="h1">
				{$format('label_dash_empty_description', {
					default: 'Looks like your dashboard is empty.',
				})}
			</Text>
			<Text class="text-center text-color-muted">
				{$format('label_add_widget_prompt', { default: 'Add a widget to get started.' })}
			</Text>
		</div>
		<Button
			on:click={() => {
				showAddWidget = true;
				hideSelectDash = true;
			}}
		>
			{$format('button_add_widget', { default: 'Add widget' })}
		</Button>
	</div>
{:else}
	<DashLayout
		bind:isMobile
		hideLeftContent={isMobile && dashItemsLeft?.length === 0}
		hideRightContent={isMobile && dashItemsRight?.length === 0}
	>
		<svelte:fragment slot="leftContent">
			{#if $edit}
				<DashActionArea
					class="mb-l"
					text={$format('button_add_widget', { default: 'Add widget' })}
					on:click={() => {
						if (isMobile) {
							editColumn = 'right';
						} else {
							editColumn = 'left';
						}
						showAddWidget = true;
					}}
				/>
			{/if}
			<CardGrid
				dndSettings={{
					items: dashItemsLeft,
					flipDurationMs: flipDurationMs,
					dragDisabled: dragDisabledLeft,
				}}
				editMode={$edit}
				noPaddingBottom={isMobile && dashItemsRight.length > 0}
				{...staticCardGridConfig}
				gridGap={isMobile ? 'm' : 'l'}
				{...isMobile && dashItemsRight.length > 0 ? { class: 'pb-0' } : {}}
				on:consider={handleReorderLeft}
				on:finalize={handleReorderLeftEnd}
			>
				{#each dashItemsLeft as item, i (item.id)}
					<div class:dnd-placeholder={item.isDndShadowItem}>
						<DashWidgetContainer
							title={item.showWidgetTitle === false ? '' : item.title}
							editMode={$edit}
							on:handle-drag-start={handleStartDragLeft}
							on:handle-drag-end={handleEndDragLeft}
						>
							<DashWidgetMoreBtn
								slot="titleRight"
								editItem={item}
								on:delete-widget-finish={(e) => {
									const deleteWidgetID = e?.detail?.id;
									dashItemsLeft = dashItemsLeft.filter(
										(dashWidget) => dashWidget.id !== deleteWidgetID,
									);
								}}
							/>

							<DashWidgetGrid {...getDashWidgetGridConfig(item)}>
								<DashWidgetCard
									cards={item?.cards}
									type={item?.type}
									cameraConfig={item?.cameraConfig}
								/>
							</DashWidgetGrid>
						</DashWidgetContainer>
					</div>
				{/each}
			</CardGrid>
		</svelte:fragment>

		<svelte:fragment slot="rightContent">
			{#if $edit && !isMobile}
				<DashActionArea
					class="mb-l"
					text={$format('button_add_widget', { default: 'Add widget' })}
					on:click={() => {
						editColumn = 'right';
						showAddWidget = true;
					}}
				/>
			{/if}
			<CardGrid
				dndSettings={{
					items: dashItemsRight,
					flipDurationMs: flipDurationMs,
					dragDisabled: dragDisabledRight,
				}}
				editMode={$edit}
				{...staticCardGridConfig}
				gridGap={isMobile ? 'm' : 'l'}
				on:consider={handleReorderRight}
				on:finalize={handleReorderRightEnd}
			>
				{#each dashItemsRight as item, i (item.id)}
					<div class:dnd-placeholder={item.isDndShadowItem}>
						<DashWidgetContainer
							title={item.showWidgetTitle === false ? '' : item.title}
							editMode={$edit}
							on:handle-drag-start={handleStartDragRight}
							on:handle-drag-end={handleEndDragRight}
						>
							<DashWidgetMoreBtn
								slot="titleRight"
								editItem={item}
								on:delete-widget-finish={(e) => {
									const deleteWidgetID = e?.detail?.id;
									dashItemsRight = dashItemsRight.filter(
										(dashWidget) => dashWidget.id !== deleteWidgetID,
									);
								}}
							/>

							<DashWidgetGrid {...getDashWidgetGridConfig(item)}>
								<DashWidgetCard
									cards={item?.cards}
									type={item?.type}
									cameraConfig={item?.cameraConfig}
								/>
							</DashWidgetGrid>
						</DashWidgetContainer>
					</div>
				{/each}
			</CardGrid>
		</svelte:fragment>
	</DashLayout>
{/if}

<DashWidgetActionModal
	{hideSelectDash}
	routeParams={$routeParams}
	isAddWizard={true}
	bind:showModal={showAddWidget}
	bind:editColumn
	on:close={() => (hideSelectDash = false)}
/>
